import { useEffect, useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | null;

const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

const readPreference = (): ThemePreference => {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") {
    return stored;
  }

  return null;
};

const systemIsDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const isDark = () => {
  const preference = readPreference();
  if (preference === "dark") return true;
  if (preference === "light") return false;
  return systemIsDark();
};

const applyClass = (dark: boolean) => {
  document.documentElement.classList.toggle("dark", dark);
};

let mediaQuery: MediaQueryList | null = null;

const onSystemThemeChange = () => {
  if (readPreference() !== null) {
    return;
  }

  applyClass(isDark());
  notify();
};

const ensureSystemSubscription = () => {
  if (typeof window === "undefined") {
    return;
  }

  const next = window.matchMedia("(prefers-color-scheme: dark)");
  if (mediaQuery === next) {
    return;
  }

  if (mediaQuery) {
    mediaQuery.removeEventListener("change", onSystemThemeChange);
    mediaQuery.onchange = null;
  }

  mediaQuery = next;
  mediaQuery.addEventListener("change", onSystemThemeChange);
  mediaQuery.onchange = onSystemThemeChange;
};

export const setThemePreference = (next: ThemePreference) => {
  if (next === "light" || next === "dark") {
    localStorage.setItem("theme", next);
  } else {
    localStorage.removeItem("theme");
  }

  applyClass(isDark());
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useResolvedToasterTheme = (): "light" | "dark" | "system" => {
  const preference = useSyncExternalStore(
    subscribe,
    readPreference,
    () => null,
  );
  return preference ?? "system";
};

export const useDarkMode = () => {
  const darkMode = useSyncExternalStore(subscribe, isDark, () => false);

  useEffect(() => {
    ensureSystemSubscription();
    applyClass(darkMode);
  }, [darkMode]);

  const setDarkMode = (
    updater: boolean | ((previous: boolean) => boolean),
  ) => {
    const next = typeof updater === "function" ? updater(darkMode) : updater;
    setThemePreference(next ? "dark" : "light");
  };

  return [darkMode, setDarkMode] as const;
};
